/**
 * What the URL fragment said when the page booted.
 *
 * The route a buyer lands on is not this app's decision. GoTrue honours the
 * `redirect_to` we ask for only when that address is on the project's allow
 * list, and falls back to the project's Site URL otherwise, silently. So a
 * setting in a dashboard decides whether she arrives on `/acesso` or on the
 * sale page — and she arrived on the sale page, signed in, with nothing on
 * screen saying so.
 *
 * Reading the fragment makes the arrival work from any route, which is the
 * behaviour we want even after the dashboard is right: an allow list that
 * loses an entry should cost nobody her session.
 */
export type AuthArrival =
  | { kind: 'session'; purpose: 'access' | 'recovery' }
  | { kind: 'error'; code: string }
  | null;

/**
 * Pure, so it can be tested without a real page.
 *
 * Both shapes come back in the fragment rather than the query string: that is
 * the implicit grant, and it is why the token never reaches a server log.
 */
export function parseAuthArrival(hash: string): AuthArrival {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  if (params.get('access_token')) {
    return {
      kind: 'session',
      purpose: params.get('type') === 'recovery' ? 'recovery' : 'access'
    };
  }

  const code = params.get('error_code') ?? params.get('error');
  return code ? { kind: 'error', code } : null;
}

/**
 * Captured at module load, on purpose: `supabase-js` strips the fragment as
 * soon as it has consumed it, and the router rewrites the URL too. By the time
 * a component could ask, the answer is gone.
 */
const arrival: AuthArrival =
  typeof window === 'undefined' ? null : parseAuthArrival(window.location.hash);

export function authArrival(): AuthArrival {
  return arrival;
}
